import { TextResources } from "@portal/assets/text/TextResources";
import Typography from "@portal/components/atoms/v2/typography";
import PortfolioSelect from "@portal/components/molecules/portfolioSelect";
import { IPortfolio } from "@portal/types/api/IPortfolio";
import { formatNumberTwoDigits } from "@portal/utils/numberFormatters";
import { FC, useMemo, useState } from "react";
import { IFundFeePopoverProps } from "./types";
import { usePositionsStore } from "@portal/externalStore/hooks/usePositionsStore";
import { getSum } from "@portal/utils/positionHelper";
import Spinner from "@portal/components/atoms/v2/spinner";
import useFilteredPortfolios from "@portal/hooks/useFilteredPortfolios";
import LabelAndHelpText from "@portal/components/molecules/labelAndHelpText";
import useBankFundCosts from "@portal/hooks/useBankFundCosts";

const FundFeePopover: FC<IFundFeePopoverProps> = ({ label, custodyFeeType }) => {
  const [selectedPortfolio, setSelectedPortfolio] = useState<IPortfolio | undefined>(undefined);
  const { data: portfolios, isLoading: portfoliosIsLoading } = useFilteredPortfolios({});

  const { getFee, isLoading } = useBankFundCosts(undefined, selectedPortfolio?.portfolioId);
  const { positions } = usePositionsStore();

  const totalFundValue = useMemo(() => {
    return positions && selectedPortfolio?.portfolioId
      ? getSum(
          positions.filter(p => p.portfolioId === selectedPortfolio?.portfolioId),
          "marketValue",
        )
      : 0;
  }, [positions, selectedPortfolio?.portfolioId]);

  const fundFee = useMemo(() => getFee(custodyFeeType, totalFundValue), [custodyFeeType, totalFundValue, getFee]);

  return (
    <LabelAndHelpText label={label} overflowVisible insideSideContent>
      <div className="space-y-5">
        <Typography variant="Bold">{TextResources.FUND_BANK_FEE_HELP_TEXT}</Typography>
        <PortfolioSelect
          label={TextResources.FUND_BANK_FEE_HELP_TEXT_PORTFOLIO_LABEL}
          value={selectedPortfolio || ""}
          onChange={(portfolio: IPortfolio | "") => {
            if (portfolio) setSelectedPortfolio(portfolio);
          }}
          portfolios={portfolios ?? []}
          portfoliosIsLoading={portfoliosIsLoading}
        />
        <div className="flex justify-between">
          <Typography variant="Body">{TextResources.FUND_BANK_FEE}</Typography>
          {isLoading ? (
            <Spinner size="small" />
          ) : (
            <Typography variant="Bold">{fundFee !== undefined ? `${formatNumberTwoDigits(fundFee)}%` : "-"}</Typography>
          )}
        </div>
      </div>
    </LabelAndHelpText>
  );
};

export default FundFeePopover;
